import ExternalLabResultPatientView from "issara-sdk/apis/ExternalLabResultPatientView_apps_LAB";

// USERS
import UserPermissionView from "issara-sdk/apis/UserPermissionView_users";

// serializer
import ReceiptSerializer from "issara-sdk/types/ReceiptSerializer_apps_BIL";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import InvoiceItemImproveSerializer from "issara-sdk/types/InvoiceItemImproveSerializer_apps_BIL";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import moment from "moment";
import { adToBe, convertBEDateToADDashDate } from "react-lib/utils/dateUtils";
import { HandlePrintReceipt } from "../../HISV3/BIL/sequence/BillPayment";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR_DATA: "CLEAR_DATA",
} as const;

export const SEQUENCE_NAME = "SharedLabResult" as const;
export const MAIN_CARD_NAME = "CardSharedLabResult" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  django?: Record<string, any>;
  SharedLabResultSequence?: SharedLabResultType;
}>;

export type SharedLabResultType = Partial<{
  sequenceIndex: "Action" | "START" | null;
  sharedLabResult: Record<string, any>[];
  sharedLabResultFilter: {
    startDate: string;
    endDate: string;
    documentNo: string;
    division: string;
  };
  selectedDocuments: Record<string,any>;
}> | null;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  SharedLabResultSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const Masters = [["division", {}]] as const;

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const filterToDate = adToBe(moment().format("DD/MM/YYYY"));

  controller.setState(
    {
      SharedLabResultSequence: {
        sequenceIndex: "Action",
        sharedLabResult: [],
        sharedLabResultFilter: {
          startDate: filterToDate,
          endDate: filterToDate,
          documentNo: "",
          division: "ALL",
        },
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH }),
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.CLEAR_DATA]: HandleClearData,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    SharedLabResultSequence: {
      ...state.SharedLabResultSequence,
      sharedLabResult: [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.SharedLabResultSequence?.sharedLabResultFilter;

  const [externalLabResultRes, externalLabResultErr, externalLabResultNet] =
    await ExternalLabResultPatientView?.get({
      patient: state.selectedPatient?.id,
      params: {
        document_date_start: convertBEDateToADDashDate(filter?.startDate),
        document_date_end: convertBEDateToADDashDate(filter?.endDate),
        document_no: filter?.documentNo,
        ...(filter?.division !== "ALL" && { scan_division: filter?.division }),
      },
      apiToken: controller.apiToken,
    });

  if (externalLabResultRes) {
    controller.setState({
      SharedLabResultSequence: {
        ...state.SharedLabResultSequence,
        sharedLabResult: externalLabResultRes?.items,
      },
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
    });
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR_DATA">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    SharedLabResultSequence: {
        sequenceIndex: "Action",
        sharedLabResult: [],
        sharedLabResultFilter: {
          startDate: "",
          endDate: "",
          documentNo: "",
          division: "ALL",
        },
      },
  });
};